import React from 'react';
import PropTypes from 'prop-types';

import {
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_TO_COLOR_MAPPING,
} from 'common/applicationAssemblyLineConstants';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_ORANGE,
  SR_ERROR_MESSAGE_THEME_RED,
} from 'client/components/SrErrorMessage/SrErrorMessage';
import SrInfoTile, {
  SR_INFO_TILE_THEME_ADMIN_GREY,
} from 'client/components/SrInfoTile/SrInfoTile';

function getCreditScores({ assemblyLine, householdApplication }) {
  const requiredCreditScore = assemblyLine.tenantScreeningCriteria?.creditScore;

  const tenantAlertReport = assemblyLine.tenantAlertReports
    ? assemblyLine.tenantAlertReports.find((tar) => tar.userId === householdApplication.userId)
    : null;

  return {
    requiredCreditScore,
    creditScore: tenantAlertReport?.creditScore,
    hasTenantAlert: !!tenantAlertReport,
  };
}

const SrApplicationAssemblyLineStepCreditScore = ({
  assemblyLine,
  householdApplication,
}) => {
  const {
    requiredCreditScore,
    creditScore,
    hasTenantAlert,
  } = getCreditScores({ assemblyLine, householdApplication });

  const status = SrApplicationAssemblyLineStepCreditScore.getStatus({
    assemblyLine,
    householdApplication,
  });

  const creditScoreDom = (
    <SrBox display='inlineBlock'>
      <SrFlex
        border='1px solid #EAEBEC'
        borderRadius='8px'
        padding='16px'
      >
        <SrInfoTile
          theme={SR_INFO_TILE_THEME_ADMIN_GREY}
          label='Applicant Credit Score'
          value={hasTenantAlert
            ? creditScore || 'Credit Score Not Found'
            : 'Tenant Alert Not Ran'
          }
          valueColor={SR_APPLICATION_ASSEMBLY_LINE_STATUS_TO_COLOR_MAPPING[status]}
        />
        <SrBox height='35px' width='1px' backgroundColor='#EAEBEC' margin='0 24px' />
        <SrInfoTile
          theme={SR_INFO_TILE_THEME_ADMIN_GREY}
          label='Required Credit Score'
          value={requiredCreditScore ? `${requiredCreditScore}+` : '--'}
        />
      </SrFlex>
    </SrBox>
  );

  const statusNotQualified = status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED;
  const statusInProgress = status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;

  const informationRequiredDom = (
    <SrBox marginTop='22px'>
      <SrText
        color={statusNotQualified ? 'red' : 'orange300'}
        font='greycliff'
        weight='700'
        size='18'
        lineHeight='24px'
        display='block'
      >
        Information Required
      </SrText>
      <SrBox marginTop='8px' maxWidth='468px'>
        <SrErrorMessage
          theme={statusNotQualified ? SR_ERROR_MESSAGE_THEME_RED : SR_ERROR_MESSAGE_THEME_ORANGE}
        >
          Applicant Credit Score
          <br />
          {hasTenantAlert && !creditScore
            ? (
              'The applicant’s credit score was not found. ' +
              'Please re-run their tenant alert.'
            )
            : (
              'The applicant’s credit score doesn’t meet the requirements. ' +
              'Please ask the applicant to provide a Guarantor with their application and give them ' +
              'instructions on how to do so. If the credit score is close please contact your ' +
              'Associate Account Manager.'
            )
          }
        </SrErrorMessage>
        <SrBox marginTop='16px' />
        <SrText
          color={statusNotQualified ? 'red' : 'orange300'}
          font='greycliff'
          weight='600'
          size='16'
          lineHeight='24px'
        >
          {hasTenantAlert && !creditScore
            ? (
              'If you have re-run their tenant alert, and the credit score is still not found, ' +
              'please post in the #QA Slack channel and link this household.'
            )
            : (
              'Once you\'ve asked the applicant for these changes move on to the ' +
              'next applicant in this household.'
            )
          }
        </SrText>
      </SrBox>
    </SrBox>
  );

  return (
    <SrBox minWidth='300px'>
      {creditScoreDom}
      {(statusNotQualified || statusInProgress) && informationRequiredDom}
    </SrBox>
  );
};

SrApplicationAssemblyLineStepCreditScore.propTypes = {
  assemblyLine: PropTypes.object.isRequired,
  householdApplication: PropTypes.object.isRequired,
};

SrApplicationAssemblyLineStepCreditScore.stepTitle = 'Credit Score';
SrApplicationAssemblyLineStepCreditScore.displayName = 'SrApplicationAssemblyLineStepCreditScore';

SrApplicationAssemblyLineStepCreditScore.getStatus = ({ assemblyLine, householdApplication }) => {
  const {
    requiredCreditScore,
    hasTenantAlert,
    creditScore,
  } = getCreditScores({ assemblyLine, householdApplication });

  let status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED;

  if (hasTenantAlert) {
    status = creditScore >= requiredCreditScore
      ? SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED
      : SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED;
  }

  return status;
};

export default SrApplicationAssemblyLineStepCreditScore;
